import { imageSrcSet } from "@/lib/image-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Shield,
  Wrench,
  Thermometer,
  ArrowRight,
  Phone,
  HardHat,
  MapPin,
  Droplets,
  SearchCheck,
  LifeBuoy,
  Check,
} from "lucide-react";
import homeHero from "../../assets/home-hero.avif";
import homeHeroSmall from "../../assets/home-hero-640.avif";
import homeHeroMobile from "../../assets/home-hero-mobile.avif.asset.json";
import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import renovatieHero from "../../assets/renovatie-hero.avif";
import nieuwbouwHeroSmall from "../../assets/nieuwbouw-hero-640.avif";
import overOnsHeroSmall from "../../assets/over-ons-hero-640.avif";
import renovatieHeroSmall from "../../assets/renovatie-hero-640.avif";
import nieuwbouwHero from "../../assets/nieuwbouw-hero.avif";
import overOnsHero from "../../assets/over-ons-hero.avif";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { responseTime } from "@/content/site";
import { canonical, shareImageMeta, langPageMeta } from "@/content/site";
import { Reveal } from "@/components/site/Reveal";
import { CountUpValue } from "@/components/site/CountUpValue";
import { WaterShowcase } from "@/components/site/WaterShowcase";
import { TrustBar } from "@/components/site/TrustBar";
import { CinematicHero } from "@/components/site/CinematicHero";
import { Container } from "@/components/site/Container";
import { Overline } from "@/components/site/Overline";
import { Section } from "@/components/site/Section";
import { ServiceBand } from "@/components/site/ServiceBand";
import { ProcessSteps } from "@/components/site/ProcessSteps";
import { ProjectGrid } from "@/components/site/ProjectGrid";
import { CtaSplit } from "@/components/site/CtaSplit";
import { mediaQuery } from "@/lib/media-queries";
import { publishedPageQuery, adminPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { useQuery } from "@tanstack/react-query";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const CODE_META_TITLE = "Zwembadbouw & renovatie in Limburg | Gopro Projects";
const HOME_HERO_FALLBACK = { src: homeHero, srcSet: `${homeHeroSmall} 640w, ${homeHero} 1024w`, mobileSrc: homeHeroMobile.url };
const CODE_META_DESCRIPTION =
  "Bouwkundige zwembaden van A tot Z en specialist in renovatie van polyester monoblock zwembaden. Geïsoleerd, polyester of vinylester. Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): { voorvertoning?: "1" } =>
    search["voorvertoning"] === "1" ? { voorvertoning: "1" } : {},
  loader: async ({ context, params }) => {
    const [, page] = await Promise.all([
      context.queryClient.ensureQueryData(mediaQuery("realisaties")),
      context.queryClient.ensureQueryData(publishedPageQuery("home", params.lang)),
    ]);
    return { page };
  },
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HOME_HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, "/").meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("home", "Bouwkundig zwembad met polyester bekleding van Gopro Projects"),
      ],
      links: [
        ...langPageMeta(params.lang, "/").links,
        ...heroPreloadLinks(heroPhoto),
      ],
      scripts: [faqJsonLd(faqs.home)],
    };
  },
  component: HomePage,
});

const pillars: { icon: typeof HardHat; title: string; desc: string; href: PublicPath }[] = [
  {
    icon: HardHat,
    title: "Bouwkundige zwembaden",
    desc: "Maatwerk van ontwerp tot oplevering. Elke kuip wordt geïsoleerd met 3 cm Recticel en afgewerkt met polyester of vinylester in een kleur naar keuze.",
    href: "/nieuwbouw",
  },
  {
    icon: Shield,
    title: "Polyester renovatie",
    desc: "Specialist in monoblock zwembaden — osmose, verkleuring en lekkages. Deze renovaties vormen ongeveer de helft van onze activiteiten.",
    href: "/renovatie",
  },
  {
    icon: Wrench,
    title: "Techniek & lekdetectie",
    desc: "Filtratie, leidingwerk, lekdetectie en onderhoud voor alle zwembadtypes. Snelle diagnose en duurzame herstellingen.",
    href: "/technieken",
  },
  {
    icon: Thermometer,
    title: "Warmtepompen & afdekkingen",
    desc: "Energiezuinige verwarming, OCEA rolluiken, AURA overkappingen en boordstenen op maat.",
    href: "/warmtepompen-afdekkingen",
  },
];

const bandItems = [
  {
    icon: Droplets,
    title: "Bouwkundige zwembaden",
    text: "Op maat gebouwd, geïsoleerd en afgewerkt met polyester of vinylester.",
  },
  {
    icon: Shield,
    title: "Renovatie monoblock",
    text: "Herstel van osmose, verkleuring en verouderde bekleding.",
  },
  {
    icon: Wrench,
    title: "Zwembadtechniek",
    text: "Filtratie, leidingwerk en sturing als één betrouwbaar systeem.",
  },
  {
    icon: SearchCheck,
    title: "Lekdetectie",
    text: "Eerst de oorzaak zoeken, daarna gericht herstellen.",
  },
  {
    icon: Thermometer,
    title: "Warmtepompen",
    text: "Energiezuinig verwarmen voor een langer zwemseizoen.",
  },
  {
    icon: HardHat,
    title: "Rolluiken & overkappingen",
    text: "OCEA-rolluiken en AURA-overkappingen, op maat geplaatst.",
  },
  {
    icon: LifeBuoy,
    title: "Nazorg & onderhoud",
    text: "Winter- en zomeronderhoud, met één vast aanspreekpunt.",
  },
];

const processSteps = [
  { title: "Kennismaking", text: "We bekijken uw bad of uw tuin ter plaatse en luisteren naar wat u wil bereiken." },
  { title: "Ontwerp & voorstel", text: "U krijgt een helder voorstel met afmetingen, techniek, afwerking en prijs." },
  { title: "Realisatie", text: "Eén ploeg, één planning: grondwerk, kuip, isolatie, bekleding en techniek." },
  { title: "Oplevering & nazorg", text: "We leggen de installatie uit en blijven bereikbaar voor onderhoud en herstel." },
];

const highlights = [
  { value: "10+", label: "jaar ervaring" },
  { value: "A-Z", label: "totaalprojecten" },
  { value: "3 cm", label: "Recticel isolatie" },
  { value: "24u", label: "reactietijd offerte" },
];

const insulationBenefits = [
  "Minder warmteverlies naar de grond, dus lagere verbruikskosten van uw warmtepomp",
  "Het water blijft langer op temperatuur, ook na een koude nacht",
  "Een langer zwemseizoen, van het vroege voorjaar tot in het najaar",
  "Aangenamer zwemcomfort: geen koude betonwanden tegen uw voeten",
];

const strengths = [
  "Technisch afgestemde uitvoering",
  "Duurzame en energiezuinige keuzes",
  "Heldere afspraken en planning",
  "Bereikbare nazorg",
];

function HomePage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const { data: assets } = useSuspenseQuery(mediaQuery("realisaties"));
  const { data: gepubliceerd } = useSuspenseQuery(publishedPageQuery("home", lang));
  // Voorvertoning toont het concept enkel aan wie is aangemeld; bezoekers zien het niet.
  const concept = useQuery({ ...adminPageQuery("home", "nl"), enabled: voorvertoning === "1" });
  const c = pageContent(voorvertoning === "1" ? (concept.data ?? gepubliceerd) : gepubliceerd);

  const projectPhotos = assets.slice(0, 6).map((asset) => ({
    src: asset.url,
    variants: asset.variants,
    alt: asset.alt ?? "Gerealiseerd bouwkundig zwembad van Gopro Projects in Limburg",
  }));

  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HOME_HERO_FALLBACK);
  const vakmanschap = c.section("vakmanschap");

  return (
    <div className="flex flex-col">
      <CinematicHero
        overline={c.veld("hero", "overline", "Zwembadbouwer in Limburg & Zuid-Nederland")}
        title={c.veld("hero", "titel", "Bouwkundige zwembaden & renovatie van polyester baden")}
        subtitle={c.rijkInline("hero", "Van een verouderd zwembad naar een moderne, duurzame zwemervaring.")}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageMobileSrc={heroPhoto.mobileSrc}
        imageAlt={c.veld("hero", "alt", "Bouwkundig zwembad met polyester bekleding in een tuin in Limburg")}
      >
        <div className="flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
            <LocaleLink to={c.veld("hero", "knopLink", "/offerte") as PublicPath}>
              {c.veld("hero", "knopTekst", "Offerte aanvragen")}
            </LocaleLink>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-primary-foreground/60 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
          >
            <LocaleLink to={c.veld("hero", "knop2Link", "/realisaties") as PublicPath}>
              {c.veld("hero", "knop2Tekst", "Bekijk realisaties")}
            </LocaleLink>
          </Button>
        </div>
        <div className="mt-10 grid max-w-2xl grid-cols-2 gap-x-6 gap-y-5 border-t border-primary-foreground/20 pt-6 sm:grid-cols-4">
          {highlights.map((highlight, index) => (
            <Reveal key={highlight.label} delay={index * 80}>
              <CountUpValue value={highlight.value} className="block font-display text-2xl font-semibold text-primary-foreground" />
              <span className="mt-1 block text-[13px] font-semibold uppercase leading-4 tracking-[0.16em] text-secondary">
                {highlight.label}
              </span>
            </Reveal>
          ))}
        </div>
      </CinematicHero>

      <ServiceBand items={bandItems} headingLevel="h2" />

      <Section tone="light">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-16">
          <div>
            <Overline>{c.veld("vakmanschap", "overline", "Vakmanschap uit Maasmechelen")}</Overline>
            <h2 className="display-section mt-4 whitespace-pre-line">
              {c.veld("vakmanschap", "titel", "Uw zwembad.\nOns vakmanschap.")}
            </h2>
            {c.alineas("vakmanschap", [
              "Gopro Projects bouwt vanuit Maasmechelen bouwkundige zwembaden van de eerste grondwerken tot de laatste boordsteen, en renoveert bestaande bouwkundige en polyester monoblock zwembaden in heel Limburg en Zuid-Nederland.",
            ]).map((alinea) => (
              <p key={alinea.slice(0, 24)} className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
                <RichText waarde={alinea} inline />
              </p>
            ))}
            <Button asChild className="mt-8">
              <LocaleLink to={c.veld("vakmanschap", "knopLink", "/over-ons") as PublicPath}>
                {c.veld("vakmanschap", "knopTekst", "Meer over ons")} <ArrowRight className="ml-1 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
          <div className="relative">
            <img
              src={vakmanschap?.mediaUrl ?? nieuwbouwHero}
              srcSet={vakmanschap?.mediaUrl ? imageSrcSet(vakmanschap.mediaVariants) : `${nieuwbouwHeroSmall} 640w, ${nieuwbouwHero} 1024w`}
              sizes="(min-width: 1024px) 55vw, 100vw"
              alt={c.veld("vakmanschap", "alt", "Afgewerkt bouwkundig zwembad met lichtgrijze polyester bekleding")}
              width={1024}
              height={768}
              loading="lazy"
              decoding="async"
              className="aspect-[4/3] w-full object-cover"
            />
            <div className="bg-primary p-6 text-primary-foreground sm:absolute sm:bottom-6 sm:right-6 sm:max-w-sm sm:p-8">
              <p className="font-display text-base font-semibold">Waarom kiezen voor Gopro Projects?</p>
              <ul className="mt-4 space-y-2 text-sm leading-6">
                {c.lijst("vakmanschap", strengths).map((item) => (
                  <li key={item} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-secondary" strokeWidth={1.75} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <section className="bg-primary text-primary-foreground">
        <Container className="grid gap-10 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 lg:py-24">
          <div className="grid grid-cols-2">
            {c
              .fotos("renovatie", [
                {
                  src: renovatieHero,
                  srcSet: `${renovatieHeroSmall} 640w, ${renovatieHero} 1024w`,
                  alt: "Polyester monoblock zwembad met osmose en verkleuring voor de renovatie",
                },
                { src: overOnsHero, srcSet: `${overOnsHeroSmall} 640w, ${overOnsHero} 1024w`, alt: "Hetzelfde zwembad na renovatie met nieuwe polyester bekleding" },
              ])
              .slice(0, 2)
              .map((foto, positie) => (
                <img
                  key={foto.src}
                  src={foto.src}
                  srcSet={foto.srcSet}
                  sizes="(min-width: 1024px) 25vw, 50vw"
                  alt={foto.alt}
                  width={1024}
                  height={768}
                  loading="lazy"
                  decoding="async"
                  className={`h-full min-h-72 w-full object-cover ${positie === 0 ? "opacity-70" : ""}`}
                />
              ))}
          </div>
          <div>
            <Overline onDark>{c.veld("renovatie", "overline", "Specialist in renovatie")}</Overline>
            <h2 className="display-section mt-4">{c.veld("renovatie", "titel", "Renovatie van monoblock zwembaden")}</h2>
            {c.alineas("renovatie", [
              "Renovatie van polyester monoblock zwembaden met osmose of verkleuring is onze grootste niche en goed voor ongeveer de helft van onze activiteiten. Het is werk dat weinig bedrijven nog uitvoeren — wij doen het elke week.",
            ]).map((alinea) => (
              <p key={alinea.slice(0, 24)} className="mt-6 max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">
                <RichText waarde={alinea} inline tone="donker" />
              </p>
            ))}
            <Button asChild className="mt-8 bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to={c.veld("renovatie", "knopLink", "/renovatie") as PublicPath}>
                {c.veld("renovatie", "knopTekst", "Ontdek zwembadrenovatie")} <ArrowRight className="ml-1 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
        </Container>
      </section>

      {projectPhotos.length > 0 && (
        <Section tone="light">
          <Overline>{c.veld("realisaties", "overline", "Realisaties")}</Overline>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="display-section">{c.veld("realisaties", "titel", "Een selectie van onze projecten")}</h2>
              <p className="mt-4 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
                {c.rijkInline("realisaties",
                  "Beelden uit onze eigen werven: bouwkundige zwembaden en gerenoveerde baden in Limburg en Zuid-Nederland.",
                )}
              </p>
            </div>
            <Button asChild variant="outline">
              <LocaleLink to={c.veld("realisaties", "knopLink", "/realisaties") as PublicPath}>
                {c.veld("realisaties", "knopTekst", "Bekijk alle realisaties")} <ArrowRight className="ml-1 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
          <ProjectGrid className="mt-10" items={projectPhotos} />
        </Section>
      )}

      <ProcessSteps
        title={c.veld("proces", "titel", "Van eerste gesprek tot afgewerkt bad")}
        intro={c.rijkInline("proces", "We begeleiden u stap voor stap, met duidelijke afspraken en één aanspreekpunt.")}
        steps={processSteps}
      />

      <CtaSplit
        overline={c.veld("cta", "overline", "Klaar voor uw zwembadproject?")}
        title={c.veld("cta", "titel", "Neem vandaag nog contact met ons op")}
        text={c.rijkInline("cta",
          `Of het nu om een nieuw bouwkundig zwembad of een renovatie gaat, we denken graag met u mee. We contacteren u ${responseTime.within}.`,
        )}
        imageSrc={
          c.foto("cta", homeHero, "Verlicht bouwkundig zwembad in een aangelegde tuin in Limburg").src
        }
        imageAlt={c.foto("cta", homeHero, "Verlicht bouwkundig zwembad in een aangelegde tuin in Limburg").alt}
      />

      <TrustBar />

      <Section tone="light">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
          <div>
            <Overline>{c.veld("wat-doet", "overline", "Alles in eigen hand")}</Overline>
            <h2 className="display-section mt-4">{c.veld("wat-doet", "titel", "Wat doet Gopro Projects?")}</h2>
            {c.alineas("wat-doet", [
              "Gopro Projects is een zwembadbedrijf uit Maasmechelen (Limburg) dat bouwkundige zwembaden bouwt als totaalproject en gespecialiseerd is in het renoveren van bestaande bouwkundige baden en polyester monoblock zwembaden.",
              "Wij verzorgen de volledige keten: grondwerken, betonkuip, 3 cm Recticel isolatie, polyester of vinylester bekleding in een kleur naar keuze, filtratie en leidingwerk, verlichting, warmtepomp, rolluik of overkapping, boordstenen en — indien gewenst — het aansluitende terras. Ook wie zelf bouwt kan bij ons terecht: wij begeleiden zelfbouwers en plaatsen de bekleding in hun eigen kuip.",
            ]).map((alinea) => (
              <p key={alinea.slice(0, 24)} className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
                <RichText waarde={alinea} inline />
              </p>
            ))}
          </div>
          <aside className="border-l-2 border-brand-deep-water bg-surface p-8 lg:p-10">
            <div className="flex items-center gap-3 text-brand-deep-water">
              <MapPin className="h-6 w-6" strokeWidth={1.75} />
              <Overline>{c.veld("werkgebied", "overline", "Vanuit Maasmechelen")}</Overline>
            </div>
            <h3 className="mt-5 text-xl font-semibold">{c.veld("werkgebied", "titel", "Werkgebied")}</h3>
            {c.alineas("werkgebied", [
              "Vanuit onze thuisbasis in Maasmechelen werken wij in heel Limburg — onder meer Genk, Hasselt, Bilzen, Lanaken, Dilsen-Stokkem, Bree, Tongeren, Sint-Truiden en Beringen — en in de grensstreek van Zuid-Nederland, met Maastricht, Sittard-Geleen, Heerlen en Roermond.",
              "Korte afstanden betekenen snelle service: voor een lekkage of een dringende herstelling staan wij vlot bij u voor de deur.",
            ]).map((alinea) => (
              <p key={alinea.slice(0, 24)} className="mt-4 text-base leading-7 text-on-surface-variant">
                <RichText waarde={alinea} inline />
              </p>
            ))}
            <Button asChild className="mt-8 w-full">
              <LocaleLink to={c.veld("werkgebied", "knopLink", "/offerte") as PublicPath}>
                {c.veld("werkgebied", "knopTekst", "Vraag een gratis offerte")}
              </LocaleLink>
            </Button>
          </aside>
        </div>
      </Section>

      <section className="bg-primary text-primary-foreground">
        <Container className="py-16 lg:py-24">
          <div className="max-w-2xl">
            <Overline onDark>{c.veld("pijlers", "overline", "Alles technisch afgestemd")}</Overline>
            <h2 className="display-section mt-4">{c.veld("pijlers", "titel", "Totaalaanpak in 4 pijlers")}</h2>
            <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("pijlers",
                "Elke fase van uw zwembadproject wordt door ons gecoördineerd: van structuur en isolatie tot techniek, kleur en afwerking.",
              )}
            </p>
          </div>
          <div className="mt-10 grid divide-y divide-primary-foreground/15 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
            {pillars.map((pillar, index) => (
              <Reveal key={pillar.title} delay={index * 90} className="h-full">
                <article className="group flex h-full flex-col items-start px-0 py-8 sm:px-6 lg:px-8 lg:first:pl-0 lg:last:pr-0">
                  <pillar.icon className="h-11 w-11 text-secondary" strokeWidth={1.75} />
                  <h3 className="mt-5 text-lg font-semibold">{pillar.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-primary-foreground/75">{pillar.desc}</p>
                  <LocaleLink
                    to={pillar.href}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-secondary focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-secondary"
                  >
                    Meer info <ArrowRight className="h-4 w-4" />
                  </LocaleLink>
                </article>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <Section tone="mist">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Overline>{c.veld("isolatie", "overline", "Isolatie als vaste basis")}</Overline>
            <h2 className="display-section mt-4">{c.veld("isolatie", "titel", "Waarom wij elk zwembad isoleren")}</h2>
            <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("isolatie",
                "Een geïsoleerd zwembad houdt zijn warmte veel langer vast. Daarom krijgt elke kuip bij ons standaard 3 cm Recticel isolatie — vloer én wanden — nog vóór de polyester of vinylester bekleding wordt aangebracht.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              {c.lijst("isolatie", insulationBenefits).map((item) => (
                <li key={item} className="flex items-start gap-3 py-4">
                  <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 bg-secondary" />
                  <span className="text-sm leading-6">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-border pt-8 lg:border-l lg:border-t-0 lg:pl-16 lg:pt-0">
            <Overline>{c.veld("bekleding", "overline", "Naadloze afwerking")}</Overline>
            <h2 className="display-section mt-4">{c.veld("bekleding", "titel", "Polyester of vinylester?")}</h2>
            {c.alineas("bekleding", [
              "Beide zijn naadloze, volledig waterdichte bekledingen die ter plaatse in meerdere lagen worden aangebracht en afgewerkt met een gekleurde topcoat. Polyester is de klassieke, bewezen keuze. Vinylester is de hoogwaardigere variant: die hars is duidelijk beter bestand tegen osmose en dus tegen vocht dat op lange termijn in de bekleding dringt.",
              "Omdat de bekleding naadloos is, zijn er geen plooien of lasnaden zoals bij een liner, en kunt u vrij kiezen uit een ruim kleurengamma — van lichtgrijs en zandtinten tot dieper blauw. De kleur bepaalt sterk hoe het water er uiteindelijk uitziet.",
            ]).map((alinea) => (
              <p key={alinea.slice(0, 24)} className="mt-6 text-[17px] leading-7 text-on-surface-variant">
                <RichText waarde={alinea} inline />
              </p>
            ))}
            <LocaleLink
              to={c.veld("bekleding", "knopLink", "/nieuwbouw") as PublicPath}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-deep-water focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-ring"
            >
              {c.veld("bekleding", "knopTekst", "Alles over onze nieuwbouwzwembaden")} <ArrowRight className="h-4 w-4" />
            </LocaleLink>
          </div>
        </div>
      </Section>

      <WaterShowcase />

      <section className="bg-primary py-16 text-primary-foreground">
        <Container className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <Overline onDark>{c.veld("contact", "overline", "Vrijblijvend advies")}</Overline>
            <h2 className="display-section mt-4">{c.veld("contact", "titel", "Klaar om uw tuin te transformeren?")}</h2>
            <p className="mt-4 max-w-xl text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("contact",
                `Bel of WhatsApp ons voor een vrijblijvend adviesgesprek. Wij contacteren u ${responseTime.within}.`,
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <a href="tel:+32484976477">
                <Phone className="mr-2 h-4 w-4" /> 0484 97 64 77
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <LocaleLink to={c.veld("contact", "knopLink", "/offerte") as PublicPath}>
                {c.veld("contact", "knopTekst", "Offerte aanvragen")}
              </LocaleLink>
            </Button>
          </div>
        </Container>
      </section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.home} />
    </div>
  );
}

